import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

const inputVariants = cva(
  [
    'flex h-10 w-full rounded border px-2 py-4',
    'font-outfit text-[13px] font-normal leading-[1.3]',
    'transition-colors duration-200',
    'focus:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-grey-400 bg-transparent',
          'text-grey-400 placeholder:text-grey-400',
          'focus:border-dawn-black focus:bg-orange-100 focus:text-dawn-black',
        ],
        filled: [
          'border-dawn-black bg-transparent',
          'text-dawn-black placeholder:text-grey-400',
          'focus:bg-orange-100',
        ],
        error: [
          'border-error bg-transparent',
          'text-dawn-black placeholder:text-grey-400',
          'focus:bg-orange-100',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Whether the input has an error */
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, error, ...props }, ref) => {
    // Automatically use error variant if error prop is true
    const computedVariant = error ? 'error' : variant;

    return (
      <input
        type={type}
        className={cn(inputVariants({ variant: computedVariant, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
